/**
 * CustomerOverview — premium creator-access hub overview.
 *
 * Pulls everything from /api/portal/customer/me. Real DB data only:
 *  - recent purchases (last 3)
 *  - active memberships
 *  - upcoming experiences (webinars/bookings)
 *  - onboarding progress (steps the buyer can complete)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  ShoppingBag, Receipt, Heart, Calendar, ArrowRight,
  Sparkles, PlayCircle, ExternalLink, CheckCircle2, Loader2,
} from "lucide-react";

const _money = (n, c = "USD") => {
  const sym = c === "USD" ? "$" : c === "EUR" ? "€" : c === "GBP" ? "£" : c === "INR" ? "₹" : "";
  return `${sym}${Number(n || 0).toFixed(0)}`;
};
const _date = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" }); }
  catch { return ""; }
};

function OnboardingChecklist({ data }) {
  const steps = [
    { id: "purchase", label: "Make your first purchase", done: (data.total_orders || 0) > 0 },
    { id: "access", label: "Open your library", done: (data.purchases || []).length > 0 && data._library_opened },
    { id: "event", label: "Attend an experience", done: (data.webinars || []).some(w => w.attended) },
    { id: "membership", label: "Activate a membership", done: (data.subscriptions || []).some(s => s.status === "active") },
  ];
  const completed = steps.filter(s => s.done).length;
  const pct = Math.round((completed / steps.length) * 100);
  return (
    <div className="surface p-6" data-testid="overview-onboarding">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-sm font-bold text-ink inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-ai" /> Getting started</h3>
        <span className="text-xs font-semibold text-ai">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden mb-4"><div className="h-full rounded-full ashra-gradient-bg" style={{ width: `${pct}%` }} /></div>
      <ul className="space-y-2">
        {steps.map((s) => (
          <li key={s.id} className="flex items-center gap-2 text-sm">
            <CheckCircle2 className={`h-4 w-4 ${s.done ? "text-emerald-500" : "text-zinc-300"}`} />
            <span className={s.done ? "text-ink-soft line-through decoration-zinc-300" : "text-ink-soft"}>{s.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function CustomerOverview() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/portal/customer/me").then(r => setD(r.data)).catch(() => setD(null)); }, []);

  if (!d) return <div className="text-meta py-10 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="surface p-5">
      <div className={`h-9 w-9 rounded-xl grid place-items-center ${color} mb-3`}><Icon className="h-4 w-4" /></div>
      <p className="text-[11px] uppercase tracking-wider text-meta font-semibold">{label}</p>
      <p className="mt-1 font-display text-2xl font-extrabold text-ink">{value}</p>
    </div>
  );

  const purchases = d.purchases || [];
  const memberships = d.memberships || [];
  const upcoming = [...(d.webinars || []), ...(d.bookings || [])]
    .filter((e) => {
      const at = e.at || e.starts_at;
      try { return at && new Date(at).getTime() > Date.now(); } catch { return false; }
    })
    .slice(0, 3);
  const hasAnything = purchases.length || memberships.length || upcoming.length;

  return (
    <div data-testid="customer-overview">
      <div className="mb-8">
        <p className="text-sm text-meta">Welcome back,</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">{user?.name?.split(" ")[0] || "there"}</h1>
        <p className="text-sm text-meta mt-1">Your purchases, memberships, and upcoming experiences — all in one place.</p>
      </div>

      {!hasAnything ? (
        <div className="surface p-10 text-center" data-testid="overview-empty">
          <Sparkles className="h-8 w-8 text-ai mx-auto" />
          <h2 className="mt-3 font-display text-xl font-bold text-ink">Your access hub is ready</h2>
          <p className="mt-1 text-sm text-meta max-w-md mx-auto">When a creator gives you access to a product, membership, or live experience, it appears here for instant access.</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat icon={ShoppingBag} label="Purchases" value={d.total_orders || 0} color="bg-ai-soft text-ai" />
            <Stat icon={Receipt} label="Total spent" value={_money(d.total_spent, "USD")} color="bg-success-soft text-success" />
            <Stat icon={Heart} label="Memberships" value={d.active_subscriptions || 0} color="bg-rose-100 text-rose-600" />
            <Stat icon={Calendar} label="Upcoming" value={upcoming.length} color="bg-amber-100 text-amber-700" />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 surface p-6" data-testid="overview-recent-purchases">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2"><PlayCircle className="h-4 w-4 text-ai" /> Continue access</h3>
                <Link to="/customer/library" className="text-xs text-ai font-bold hover:underline inline-flex items-center gap-0.5">Open library <ArrowRight className="h-3 w-3" /></Link>
              </div>
              {purchases.length === 0 ? (
                <p className="text-sm text-meta">Buy something to see it here.</p>
              ) : (
                <div className="space-y-3">
                  {purchases.slice(0, 3).map((p) => (
                    <div key={p.id} className="rounded-2xl border border-line p-4 hover:bg-zinc-50 transition flex items-center justify-between gap-3" data-testid={`overview-purchase-${p.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-ink">{p.asset_title}</p>
                        <p className="text-[11px] text-meta">{(p.asset_type || "").replace("_", " ")} · purchased {_date(p.created_at)}</p>
                      </div>
                      {(p.delivery?.redirect_url || p.delivery?.pdf_url || p.delivery?.drive_url || p.delivery?.video_url) ? (
                        <a href={p.delivery.redirect_url || p.delivery.pdf_url || p.delivery.drive_url || p.delivery.video_url} target="_blank" rel="noreferrer" className="text-xs font-semibold rounded-full ashra-gradient-bg text-white px-3 py-1.5 inline-flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Open
                        </a>
                      ) : (
                        <Link to={`/customer/purchases`} className="text-xs font-semibold rounded-full bg-zinc-100 text-ink-soft px-3 py-1.5 inline-flex items-center gap-1">View</Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="surface p-6" data-testid="overview-upcoming">
                <h3 className="font-display text-sm font-bold text-ink mb-3 inline-flex items-center gap-2"><Calendar className="h-4 w-4 text-ai" /> Upcoming</h3>
                {upcoming.length === 0 ? (
                  <p className="text-xs text-meta">No upcoming sessions. New experiences will appear here.</p>
                ) : (
                  <div className="space-y-2">
                    {upcoming.map((u, i) => (
                      <div key={u.id || i} className="rounded-xl border border-line p-3">
                        <p className="text-[10px] uppercase tracking-wider text-ai font-bold">{u.kind || "Session"}</p>
                        <p className="text-sm font-semibold mt-1 text-ink">{u.title || u.product_name}</p>
                        <p className="text-xs text-meta">{_date(u.at || u.starts_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {memberships.length > 0 ? (
                <div className="rounded-3xl bg-gradient-to-br from-violet-100 via-fuchsia-50 to-pink-100 border border-violet-200 p-6" data-testid="overview-membership-card">
                  <p className="text-xs text-ai font-bold uppercase tracking-wider">Membership</p>
                  <p className="mt-2 font-display text-2xl font-extrabold text-ink">{memberships[0].name || memberships[0].title || "Active membership"}</p>
                  <p className="mt-1 text-xs text-ink-soft leading-relaxed">{memberships[0].description || "Premium access · ongoing perks"}</p>
                  <Link to="/customer/memberships" className="mt-4 block text-center rounded-full ashra-gradient-bg text-white text-sm font-semibold py-2.5 shadow-sm hover:shadow-md transition">Open memberships</Link>
                </div>
              ) : (
                <OnboardingChecklist data={d} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
